// A collection of useful bulk download types and functions.
// The core functionality of bulk downloading is found near the EoF.
import routes from 'sitewide-js/core_pre_webpack/routes';
import { Aedu } from 'global';

import { WorkId } from 'js/liswp/types';

// The offical source (keys) of bulk download triggers.
// Mapping from trigger name to short impression trigger name.
// Note that impression sources are limited to 64 characters.
// Info on current and deprecated triggers:
//   https://github.com/academia-edu/wiki/blob/master/Wiki-Pages/Bulk-Download.md
const BULK_DOWNLOAD_IMPRESSION_TRIGGERS = {
  'swp-download-button': 'swp_d',
  'swp-bulk-download-button': 'swp_bd',
  'swp-bulk-download-preview-button': 'swp_bd_preview',
  'swp-right-rail-related-work': 'swp_rw',
  'swp-auto-download': 'swp_auto_d',
  'swp-auto-bulk-download': 'swp_auto_bd',
  'swp-auto-bulk-download-books': 'swp_auto_bdb',
  'sutd-bulk-download': 'sutd',
  'swp-related-works-grid': 'swp_rwg',
  'swp-post-download-upsell': 'swp-post-download-upsell',
  // LISWP block-page ladder, hero/royal_blue variant (PR #51041)
  'swp-royal-blue-hero': 'swp_rbh',
  // Single-download button in the royal_blue hero (useDownload derives this
  // `${source}-work-card-download-button` trigger). Registered so it behaves
  // like the legacy swp-toolbar-work-card-download-button.
  'swp-royal-blue-hero-work-card-download-button': 'rbh_wc_d',
  // LISWP block-page ladder, hero/side_by_side variant (variant 01).
  'swp-side-by-side-hero': 'swp_sbs',
  'swp-side-by-side-hero-work-card-download-button': 'sbs_wc_d',
  // LISWP block-page ladder, hero/related_papers variant (variant 02).
  'swp-related-papers-hero': 'swp_rph',
  'swp-related-papers-hero-work-card-download-button': 'rph_wc_d',
  // LISWP block-page ladder, hero/gold_card variant (variant 04).
  'swp-gold-card-hero': 'swp_gc',
  'swp-gold-card-hero-work-card-download-button': 'gc_wc_d',
  // LISWP block-page ladder, hero/tabs variant (variant 05).
  'swp-tabs-hero': 'swp_tabs',
  'swp-tabs-hero-work-card-download-button': 'tabs_wc_d',
  // LISWP block-page ladder, hero/pick_download variant (variant 06).
  'swp-pick-download-hero': 'swp_pkd',
  'swp-pick-download-hero-work-card-download-button': 'pkd_wc_d',

  'swp-toolbar-work-card-download-button': 'swp_wc_d',
  'swp-sticky-header-work-card-download-button': 'sh_wc_d',
  'swp-sticky-header-bulk-download-button': 'sh_bd',

  // canonical work card triggers
  'news-feed-work-card-download-button': 'nf_d',
  'news-feed-bulk-download-button': 'nf_bd',
  'search-results-work-card-download-button': 'sr_d',
  'search-results-bulk-download-button': 'sr_bd',
  'library-work-card-download-button': 'lib_d',
  'library-bulk-download-button': 'lib_bd',
  'library-pdf-packs-card': 'lib_pdfp_card',
  'recommended-pdf-packs-card': 'rec_pdfp_card',

  'sutd-new-user-discount': 'sutd_nud',

  // new recommended papers funnel
  'claiming-recommended-papers-download-button': 'ri-claiming',
};

// The work download source when triggered by a bulk download action
const BULK_DOWNLOAD_DOWNLOAD_SOURCE = {
  'swp-download-button': 'swp-toolbar',
  'swp-bulk-download-button': 'swp-bulk-download-modal',
  'swp-bulk-download-preview-button': 'swp-pdf-pack-widget',
  'swp-right-rail-related-work': 'swp_related_works_sidebar',
  'swp-related-works-grid': 'swp-related-works-grid',
  'swp-royal-blue-hero': 'swp-royal-blue-hero',
  'swp-royal-blue-hero-work-card-download-button': 'swp-royal-blue-hero',
  'swp-side-by-side-hero': 'swp-side-by-side-hero',
  'swp-side-by-side-hero-work-card-download-button': 'swp-side-by-side-hero',
  'swp-related-papers-hero': 'swp-related-papers-hero',
  'swp-related-papers-hero-work-card-download-button':
    'swp-related-papers-hero',
  'swp-gold-card-hero': 'swp-gold-card-hero',
  'swp-gold-card-hero-work-card-download-button': 'swp-gold-card-hero',
  'swp-tabs-hero': 'swp-tabs-hero',
  'swp-tabs-hero-work-card-download-button': 'swp-tabs-hero',
  'swp-pick-download-hero': 'swp-pick-download-hero',
  'swp-pick-download-hero-work-card-download-button': 'swp-pick-download-hero',
  'swp-auto-download': 'email',
  'swp-auto-bulk-download': 'swp-toolbar',
  'sutd-bulk-download': 'sutd',
  'swp-toolbar-work-card-download-button': 'swp-toolbar',
  'swp-sticky-header-work-card-download-button': 'swp-sticky-header',
  'swp-sticky-header-bulk-download-button': 'swp-sticky-header',
  'profile-download-button': 'profile',
  'news-feed-work-card-download-button': 'news-feed',
  'news-feed-bulk-download-button': 'news-feed',
  'search-results-work-card-download-button': 'search-results',
  'search-results-bulk-download-button': 'search-results',
  'library-work-card-download-button': 'library',
  'library-bulk-download-button': 'library',
  'coronavirus_page-work-card-download-button': 'coronavirus_page',
  'coronavirus_page-bulk-download-button': 'coronavirus_page',
};

export type BulkDownloadTrigger =
  keyof typeof BULK_DOWNLOAD_IMPRESSION_TRIGGERS;

export type BulkDownloadableWork = {
  id: WorkId;
  title: string;
  slug: string; // used to name the zip
  attachment: {
    url: string;
    id: number;
  };
  authors: Array<{
    displayName: string;
    photo?: string;
    id: number;
  }>;
  metadata?: { abstract?: string } | null;
  researchInterests?: Array<{ name: string }> | null;
  landingPageUrl?: string;
  doi?: string;
  licenseAllowsAttachment?: boolean;
};

/** Returns the best external fallback URL for a work with no stored attachment.
 *  Prefers the DOI resolver when a DOI is available; falls back to a Google
 *  Scholar title search otherwise. */
export function externalFallbackUrl(
  doi: string | undefined,
  title: string | undefined,
): string | undefined {
  if (doi) return `https://doi.org/${doi}`;
  if (title)
    return `https://scholar.google.com/scholar?q=${encodeURIComponent(title)}`;
  return undefined;
}

// Our version of babel doesn't support the `as const` assertion, so I have to do this instead
export const BULK_DOWNLOAD_FILTER_NAMES = (<T extends string[]>(...args: T) =>
  args)('topRelated', 'sameAuthor', 'citingThis', 'citedByThis');

export type BulkDownloadFilter = (typeof BULK_DOWNLOAD_FILTER_NAMES)[number];

export type BulkDownloadFilterCounts = { [P in BulkDownloadFilter]?: number };

export type BulkDownloadFile = {
  filename: string;
  work_id?: number;
  url: string;
  filesize: number;
};

export type BulkDownloadFilesResponse = {
  files: BulkDownloadFile[];
  zipperUrl: string;
  thumbnailUrls?: string[];
  sourceWorkTitle?: string | null;
  bundleDescription?: string;
};

export type QuotePartitions = {
  value: string;
  emphasized?: boolean;
};

export type BulkDownloadSocialProof = {
  fullName: string;
  university: string;
  userImageUrl: string;
  quotePartitions: QuotePartitions[];
};

// any triggers which should be controlled by a lookback should be added here
export function updateBulkDownloadLookback(trigger: BulkDownloadTrigger): void {
  const updateLookbackTriggerWhitelist = [
    'swp-download-button',
    'swp-auto-download',
    'swp-toolbar-work-card-download-button',
    'swp-sticky-header-work-card-download-button',
    'swp-royal-blue-hero-work-card-download-button',
    'swp-side-by-side-hero-work-card-download-button',
    'swp-related-papers-hero-work-card-download-button',
    'swp-gold-card-hero-work-card-download-button',
    'swp-tabs-hero-work-card-download-button',
    'swp-pick-download-hero-work-card-download-button',
  ];

  if (updateLookbackTriggerWhitelist.includes(trigger)) {
    Aedu.Dismissibles.recordShow('BulkDownloadUpsellModal');
  }
}

// defines the triggers which should start a download on modal close
export function giveGatedDownloadOnUpsellClose(
  trigger: BulkDownloadTrigger,
): boolean {
  const giveDownloadBlacklist = [
    'swp-bulk-download-button',
    'sutd-bulk-download',
    'swp-sticky-header-bulk-download-button',
    'news-feed-bulk-download-button',
    'search-results-bulk-download-button',
    'library-bulk-download-button',
    'swp-auto-download',
    // royal_blue "Download Full PDF Package" button: closing the package modal
    // must NOT trigger the single-PDF download (parity with
    // swp-bulk-download-button), else "No thanks"/X redirects to the file.
    'swp-royal-blue-hero',
    // side_by_side / related_papers "Download Full PDF Package" buttons —
    // same reasoning as royal_blue; all six heroes share the same handler
    // (their omission was drift, caught in the #51786 review).
    'swp-side-by-side-hero',
    'swp-related-papers-hero',
    // gold_card "Download Full PDF Package" button — same reasoning as
    // royal_blue (the package modal close must not redirect to the file).
    'swp-gold-card-hero',
    // tabs "Download PDF Pack for $1" button — same reasoning as royal_blue
    // (the package modal close must not redirect to the file).
    'swp-tabs-hero',
    // pick_download "Full PDF Package" Download button — same reasoning as
    // royal_blue (the package modal close must not redirect to the file).
    'swp-pick-download-hero',
  ];

  return !giveDownloadBlacklist.includes(trigger);
}

// the source to give to ImpressionWatcher
// https://github.com/academia-edu/wiki/blob/master/Wiki-Pages/Bulk-Download.md#impression-sources
export function bulkDownloadImpressionSource(
  mode: string,
  trigger: BulkDownloadTrigger,
): string {
  const impressionMode =
    mode === 'chooser' || mode === 'bulk-downloading' ? 'chooser' : 'upsell';
  const impressionTrigger =
    BULK_DOWNLOAD_IMPRESSION_TRIGGERS[trigger] || 'unknown';
  return `bulk_download_${impressionMode}_${impressionTrigger}`;
}

// the source to give a work download button
export function bulkDownloadDownloadSource(
  trigger: BulkDownloadTrigger,
): string {
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return BULK_DOWNLOAD_DOWNLOAD_SOURCE[trigger];
}

export function bulkDownloadImpressionTrigger(
  trigger: BulkDownloadTrigger,
): string {
  return BULK_DOWNLOAD_IMPRESSION_TRIGGERS[trigger];
}

// Naviagtes to checkout for bulk download, with url params set.
// Post-upgrade users are redirected to the work's swp where a
// bulk download will auto begin, with only the selected filters.
export function navigateToCheckout(
  trigger: BulkDownloadTrigger,
  workId: number | string,
  selectedFilters: Set<BulkDownloadFilter>,
): void {
  window.location.href = checkoutUrl(trigger, workId, selectedFilters);
}

// returns the bd checkout url with upgradeRedirect, feature, and trigger set
export function checkoutUrl(
  trigger: BulkDownloadTrigger,
  workId: number | string,
  selectedFilters: Set<BulkDownloadFilter>,
  extraParams?: { [key: string]: string },
): string {
  // if no filters are selected, select them all post-upgrade so bulk download can start
  const checkoutFilterQuery =
    Array.from(selectedFilters).length > 0
      ? Array.from(selectedFilters).join('-')
      : true;

  return routes.checkout_url({
    feature: 'BULK_DOWNLOAD',
    trigger: trigger,
    upgradeRedirect: encodeURIComponent(
      routes.show_work_url({ id: workId, bulkDownload: checkoutFilterQuery }),
    ),
    ...extraParams,
  });
}

// returns the bd marketing page url, with url params set
export function marketingPageUrl(
  trigger: BulkDownloadTrigger,
  workId: number | string,
  workTitle: string,
  filterCounts: BulkDownloadFilterCounts,
): string {
  return routes.upgrade_url({
    feature: 'bulk_download',
    trigger: trigger,
    work_title: workTitle,
    num_citing: filterCounts['citingThis'] || 0,
    num_cited: filterCounts['citedByThis'] || 0,
    num_same_author: filterCounts['sameAuthor'] || 0,
    num_top_papers: filterCounts['topRelated'] || 0,
    upgrade_redirect: encodeURIComponent(
      routes.show_work_url({ id: workId, bulkDownload: true }),
    ),
  });
}

////////////////////////////////////////////////
// Core bulk downloading functionality below //
//////////////////////////////////////////////

// Get the list of files that will constitute the bulk download for a given work id.
// If workIds is provided, downloads those specific works (used for filtered downloads).
// Otherwise, uses bucket-based selection.
//
// When workIds AND workBuckets are provided, the backend uses the bucket map
// directly to organise files into subdirectories, without re-running the
// WorksChooser query.  The frontend already knows each work's bucket from the
// preview API response.
export function getBulkDownloadFileList(
  sourceWorkId: string,
  bucketWhitelist: string[] = [],
  downloadType: 'client-side' | 'server-side',
  workIds?: number[],
  workBuckets?: Record<number, string>,
): Promise<BulkDownloadFilesResponse> {
  // Serialise the workId→bucket mapping as "id:bucket,id:bucket,…"
  const workBucketsParam =
    workBuckets && Object.keys(workBuckets).length > 0
      ? Object.entries(workBuckets)
          .map(([id, bucket]) => `${id}:${bucket}`)
          .join(',')
      : undefined;

  return fetch(
    routes.bulk_download_files_api_v0_attachments_path({
      subdomain_param: 'api',
      source_work_id: sourceWorkId,
      bucket_whitelist: Array.from(bucketWhitelist),
      download_type: downloadType,
      // If workIds provided, pass them to download exactly those works
      ...(workIds ? { work_ids: workIds.join(',') } : {}),
      // Pass bucket assignments so the backend can build matching directories
      ...(workBucketsParam ? { work_buckets: workBucketsParam } : {}),
    }),
  ).then((r) => r.json());
}

export function getBulkDownloadBooksFileList(
  query: string,
  riIds: number[],
): Promise<BulkDownloadFilesResponse> {
  return fetch(
    routes.bulk_download_books_api_v0_attachments_path({
      subdomain_param: 'api',
      query: query,
      ri_ids: riIds.join(','),
    }),
  ).then((r) => r.json());
}

export function getProfileBulkDownloadFileList(
  userId: number,
): Promise<BulkDownloadFilesResponse> {
  return fetch(
    routes.bulk_download_profile_files_api_v0_attachments_path({
      subdomain_param: 'api',
      user_id: userId,
    }),
  ).then((r) => r.json());
}

export interface ProfilePreviewPaper {
  id: number;
  title: string;
  authors: string;
  year: number | null;
  thumbnailUrl: string | null;
  viewCount: number;
  bucket: string;
  relevancy: number | null;
}

export interface ProfilePreviewResponse {
  sameAuthorCount: number;
  relatedPapersCount: number;
  totalPapersCount: number;
  totalViews: number;
  papers: ProfilePreviewPaper[];
}

export function getProfileBulkDownloadPreview(
  userId: number,
): Promise<ProfilePreviewResponse> {
  return fetch(
    routes.bulk_download_profile_preview_api_v0_attachments_path({
      subdomain_param: 'api',
      user_id: userId,
    }),
  ).then((r) => r.json());
}

// Ask the AWS Lambda zipper function to collect the given files into a zip bundle,
// then return the corresponding S3 url
export function getBulkDownloadServerSideZipUrl(
  files: { filename: string; url: string }[],
  workTitle: string,
  zipperUrl: string,
): Promise<string> {
  return fetch(zipperUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      files: files,
      title: workTitle.slice(0, 40),
    }),
  })
    .then((r) => r.json())
    .then((r) => r.uploaded_to);
}
